import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { Campaign } from '../../models/campaign'
import { CompanyTypeSlug } from '../../models/companies'
import { baseAPICall } from '../../services/baseAPICall'
import { queryClient } from '../../services/queryClient'
import { clientInstance } from '../../utils/http-client'
import { PaginatedResponse } from 'models/paginated-response'
import { ALL_CAMPAIGNS_TABLE_KEY } from 'constants/tableQueryKeys'
import { getCampaignStatsQueryKey } from 'services/campaigns'
import {
  evaluateJobTransition,
  isJobRunning,
  type JobSnapshot,
} from 'features/campaigns/campaignValidationJobTransition'

/* -------------
List Campaigns
-------------- */
export async function getCampaigns(
  signal?: AbortSignal,
  queryParams?: Record<string, any>
) {
  return baseAPICall<PaginatedResponse<Campaign>>(() =>
    clientInstance.get('contacts/campaigns/', {
      signal,
      params: {
        ...queryParams,
      },
    })
  )
}

export const useGetCampaigns = (
  signal?: AbortSignal,
  disabled?: boolean,
  queryParams?: Record<string, any>
) =>
  useQuery({
    queryKey: [ALL_CAMPAIGNS_TABLE_KEY, JSON.stringify(queryParams || {})],
    queryFn: () => getCampaigns(signal, queryParams),
    refetchOnMount: true,
    staleTime: 1000 * 60 * 5,
    enabled: !disabled,
  })

// Fetch a single campaign by id via the list endpoint's `include_ids` filter.
// Pages that route or render off a campaign id must not use useGetCampaigns()
// and .find() the target -- that returns only the unfiltered top 100 by
// -created, so any campaign past that page silently 404s.
export const useGetCampaignById = (campaignId?: number | null) => {
  const isValid =
    typeof campaignId === 'number' &&
    Number.isFinite(campaignId) &&
    campaignId > 0
  const queryParams = isValid ? { include_ids: [campaignId] } : undefined
  return useQuery({
    queryKey: [ALL_CAMPAIGNS_TABLE_KEY, JSON.stringify(queryParams || {})],
    queryFn: ({ signal }) => getCampaigns(signal, queryParams),
    enabled: isValid,
    refetchOnMount: true,
    staleTime: 1000 * 60 * 5,
    select: (data) => data?.results?.[0],
  })
}

/* -------------
Get Campaign Options
-------------- */
// This uses a dedicated lightweight endpoint (/campaigns/options/) that is
// significantly faster than the main campaigns list endpoint because it:
// - Doesn't calculate aggregations (contact counts, deal stats, TARO, etc.)
// - Doesn't perform lateral joins with contacts, deals, chains, etc.
// - Returns only minimal fields needed for dropdowns (id, name, color, type)
interface CampaignOption {
  label: string
  value: number | string
  icon?: React.JSX.Element
  color?: string
  companyTypeSlug?: CompanyTypeSlug
}

interface CampaignOptionResponse {
  id: number
  name: string
  color: string
  description: string
  type: {
    id: number
    name: string
    slug: CompanyTypeSlug
  }
}

export async function getCampaignsOptions(signal?: AbortSignal) {
  return baseAPICall<CampaignOptionResponse[]>(() =>
    clientInstance.get('contacts/campaigns/options/', { signal })
  )
}

/**
 * Parameters for useGetCampaignsOptions when using the options object API.
 */
interface UseGetCampaignsOptionsParams {
  /** Filter campaigns by subcategory ID or slug */
  typeOrTypeId?: CompanyTypeSlug | number
  /** AbortSignal for cancelling the request */
  signal?: AbortSignal
  /** Prepend a "No Campaign" option to the list */
  withNoneOption?: boolean
  /** Disable the query */
  disabled?: boolean
  /**
   * When true, returns empty array if typeOrTypeId is not provided.
   * Use this in deal/company edit modals to prevent selecting campaigns
   * when the subcategory is unknown, avoiding backend validation errors.
   */
  requireFilter?: boolean
}

/**
 * Helper to check if the first argument is an options object vs a filter value.
 */
function isOptionsObject(
  arg: CompanyTypeSlug | number | UseGetCampaignsOptionsParams | undefined
): arg is UseGetCampaignsOptionsParams {
  return (
    typeof arg === 'object' &&
    arg !== null &&
    !Array.isArray(arg) &&
    ('typeOrTypeId' in arg ||
      'requireFilter' in arg ||
      'withNoneOption' in arg ||
      'disabled' in arg ||
      'signal' in arg)
  )
}

/**
 * Fetches campaign options with optional filtering by subcategory.
 *
 * Supports two calling conventions for backwards compatibility:
 * 1. Options object: `useGetCampaignsOptions({ typeOrTypeId, requireFilter: true })`
 * 2. Positional args: `useGetCampaignsOptions(typeOrTypeId, signal, withNoneOption, disabled)`
 *
 * @param typeOrTypeIdOrOptions - Filter value or options object
 * @param signal - AbortSignal (positional API only)
 * @param withNoneOption - Add "No Campaign" option (positional API only)
 * @param disabled - Disable query (positional API only)
 * @returns React Query result with campaign options
 */
export const useGetCampaignsOptions = (
  typeOrTypeIdOrOptions?:
    | CompanyTypeSlug
    | number
    | UseGetCampaignsOptionsParams,
  signal?: AbortSignal,
  withNoneOption = false,
  disabled = false
) => {
  // Normalize to options object for consistent handling
  const options: UseGetCampaignsOptionsParams = isOptionsObject(
    typeOrTypeIdOrOptions
  )
    ? typeOrTypeIdOrOptions
    : {
        typeOrTypeId: typeOrTypeIdOrOptions,
        signal,
        withNoneOption,
        disabled,
      }

  const {
    typeOrTypeId,
    signal: optSignal,
    withNoneOption: optWithNoneOption = false,
    disabled: optDisabled = false,
    requireFilter = false,
  } = options

  return useQuery({
    queryKey: ['campaigns-options'],
    queryFn: () => getCampaignsOptions(optSignal),
    refetchOnMount: false,
    staleTime: 1000 * 60 * 5,
    enabled: !optDisabled,
    select: (data) => {
      const filteredData = filterCampaignsByType(
        data,
        typeOrTypeId,
        requireFilter
      )
      const campaignOptions = mapToCampaignOptions(filteredData)

      if (optWithNoneOption) {
        campaignOptions.unshift({ label: 'No Campaign', value: 'REMOVE' })
      }

      return campaignOptions
    },
  })
}

/**
 * Filters campaigns by subcategory type.
 *
 * @param data - Raw campaign data from API
 * @param typeOrTypeId - Subcategory ID (number) or slug (string) to filter by
 * @param requireFilter - If true and no filter provided, returns empty array
 * @returns Filtered campaign data
 */
function filterCampaignsByType(
  data: CampaignOptionResponse[],
  typeOrTypeId: CompanyTypeSlug | number | undefined,
  requireFilter: boolean
): CampaignOptionResponse[] {
  if (typeOrTypeId) {
    return data.filter((campaign) =>
      typeof typeOrTypeId === 'number'
        ? campaign.type?.id === typeOrTypeId
        : campaign.type?.slug === typeOrTypeId
    )
  }
  // No filter provided - return empty if requireFilter, otherwise all
  return requireFilter ? [] : data
}

/**
 * Maps raw campaign data to select option format.
 */
function mapToCampaignOptions(
  data: CampaignOptionResponse[]
): CampaignOption[] {
  return data.map((c) => ({
    label: c.name,
    value: c.id,
    companyTypeSlug: c.type?.slug,
    color: c.color,
    icon: (
      <div
        className="h-3 w-3 rounded-full mr-2 flex-shrink-0"
        style={{ backgroundColor: c.color }}
      />
    ),
  }))
}

/* -------------
Create Campaign
-------------- */
type CreateCampaignParams = {
  type_slug: CompanyTypeSlug
  product_id?: number
  name: string
  color: string
  description: string
}

async function createCampaign(values: CreateCampaignParams) {
  return baseAPICall<Campaign>(
    () => clientInstance.post('contacts/campaigns/create/', values),
    { successMessage: 'Campaign created successfully' }
  )
}

export const useCreateCampaign = () => {
  return useMutation({
    mutationFn: createCampaign,
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: [ALL_CAMPAIGNS_TABLE_KEY],
        exact: false,
      })
    },
    onSuccess: async () => {
      // Always refetch after error or success to ensure consistency
      await queryClient.invalidateQueries({
        queryKey: [ALL_CAMPAIGNS_TABLE_KEY],
      })
      queryClient.invalidateQueries({ queryKey: getCampaignStatsQueryKey() })
    },
  })
}

/* -------------
Edit Campaign
-------------- */
type EditCampaignParams = Partial<
  Pick<CreateCampaignParams, 'name' | 'product_id' | 'color' | 'description'>
> & {
  launch_date?: string | null
  owner?: number | null
  notification_recipient_ids?: number[]
  // Segment / opportunity type. Slug to set, or null to clear. The backend
  // serializer reads this; the `type` object below is for the optimistic cache.
  type_slug?: string | null
}

async function editCampaign(id: number, values: EditCampaignParams) {
  return baseAPICall<Campaign>(
    () => clientInstance.put(`contacts/campaigns/${id}/edit/`, values),
    { successMessage: 'Campaign updated successfully' }
  )
}

export const useEditCampaign = () => {
  return useMutation({
    // `product` and `type` are client-only objects used by the optimistic
    // cache update below; discard them here (underscore = intentionally unused)
    // so only real API fields (type_slug, product_id, etc.) reach the body.
    mutationFn: ({
      id,
      product: _product,
      type: _type,
      product_id,
      ...values
    }: EditCampaignParams & {
      id: number
      product?: { id: number; name: string }
      product_id?: number
      type?: Campaign['type']
    }) => editCampaign(id, { ...values, product_id }),

    onMutate: async (newCampaign) => {
      // Get the main campaign list query
      const mainQueryKey = [ALL_CAMPAIGNS_TABLE_KEY]
      const mainQuery = queryClient.getQueryData(mainQueryKey)

      // Cancel any outgoing refetches to avoid optimistic update being overwritten
      await queryClient.cancelQueries({
        queryKey: mainQueryKey,
        exact: false,
      })

      // Update the campaign in all related queries
      const updateCampaign = (campaign: Campaign) => ({
        ...campaign,
        name: newCampaign.name ?? campaign.name,
        type: newCampaign.type !== undefined ? newCampaign.type : campaign.type,
        description: newCampaign.description ?? campaign.description,
        color: newCampaign.color ?? campaign.color,
        launch_date:
          newCampaign.launch_date !== undefined
            ? newCampaign.launch_date
            : campaign.launch_date,
        owner_id:
          newCampaign.owner !== undefined
            ? (newCampaign.owner ?? null)
            : campaign.owner_id,
        notification_recipient_ids:
          newCampaign.notification_recipient_ids !== undefined
            ? newCampaign.notification_recipient_ids
            : campaign.notification_recipient_ids,
        product:
          newCampaign.product !== undefined
            ? newCampaign.product
            : newCampaign.product_id === undefined
              ? campaign.product
              : newCampaign.product_id === null
                ? undefined
                : campaign.product?.id === newCampaign.product_id
                  ? campaign.product
                  : campaign.product,
      })

      // Apply updates to all matching queries
      queryClient.setQueriesData(
        { queryKey: mainQueryKey, exact: false },
        (old: PaginatedResponse<Campaign> | undefined) => {
          if (!old) return old
          return {
            ...old,
            results: old.results?.map((campaign) =>
              campaign.id === newCampaign.id
                ? updateCampaign(campaign)
                : campaign
            ),
          }
        }
      )

      // Return the main query data for rollback
      return {
        prev: mainQuery,
        queryKey: mainQueryKey,
      }
    },

    onError: (_, __, context) => {
      // Restore the previous data if there was an error
      if (context?.prev && context.queryKey) {
        queryClient.setQueryData(context.queryKey, context.prev)
      }
    },
  })
}

/* -------------
Delete Campaign
-------------- */
async function deleteCampaign(id: number) {
  return baseAPICall(
    () => clientInstance.delete(`contacts/campaigns/${id}/delete/`),
    {
      successMessage: 'Campaign deleted successfully',
    }
  )
}

export const useDeleteCampaign = () => {
  return useMutation({
    mutationFn: deleteCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ALL_CAMPAIGNS_TABLE_KEY] })
      queryClient.invalidateQueries({ queryKey: getCampaignStatsQueryKey() })
    },
  })
}

/* -------------
Archive Campaign
-------------- */
async function archiveCampaign(id: number) {
  return baseAPICall(
    () => clientInstance.post(`contacts/campaigns/${id}/archive/`),
    {
      successMessage: 'Campaign archived',
    }
  )
}

export const useArchiveCampaign = () => {
  return useMutation({
    mutationFn: archiveCampaign,
    onSuccess: (_data, campaignId) => {
      queryClient.invalidateQueries({ queryKey: [ALL_CAMPAIGNS_TABLE_KEY] })
      queryClient.invalidateQueries({ queryKey: getCampaignStatsQueryKey() })
      queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] })
    },
  })
}

/* -------------
Unarchive Campaign
-------------- */
async function unarchiveCampaign(id: number) {
  return baseAPICall(
    () => clientInstance.post(`contacts/campaigns/${id}/unarchive/`),
    {
      successMessage: 'Campaign unarchived',
    }
  )
}

export const useUnarchiveCampaign = () => {
  return useMutation({
    mutationFn: unarchiveCampaign,
    onSuccess: (_data, campaignId) => {
      queryClient.invalidateQueries({ queryKey: [ALL_CAMPAIGNS_TABLE_KEY] })
      queryClient.invalidateQueries({ queryKey: getCampaignStatsQueryKey() })
      queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] })
    },
  })
}

/* -------------
Campaign Companies
(for contact request modal)
-------------- */
async function getCampaignCompanies(campaignId: number, signal?: AbortSignal) {
  return baseAPICall<CampaignCompaniesResponse>(() =>
    clientInstance.get(`contacts/campaigns/${campaignId}/companies/`, {
      signal,
    })
  )
}

export const useGetCampaignCompanies = (
  campaignId: number,
  signal?: AbortSignal
) =>
  useQuery({
    queryKey: ['campaign', campaignId, 'companies'],
    queryFn: () => getCampaignCompanies(campaignId, signal),
  })

export interface CampaignCompaniesResponse {
  results: Array<{
    id: number
    name: string
  }>
  total_count: number
}

// EmailValidity values mirror the backend EmailValidity enum.
// See server/contacts/models.py.
export type EmailValidityValue = 0 | 1 | 3 | 4 | 5 | 6 | 7

export interface CampaignLeadReviewItem {
  contact_id: number
  bison_lead_id: number | null
  full_name: string
  email: string | null
  email_validity: EmailValidityValue | null
  title: string
  company_name: string
  qa_status: 'approved' | 'flagged' | 'needs_validation' | 'rejected'
  qa_reason: string | null
  is_included: boolean
}

export interface CampaignLeadReviewResponse {
  results: CampaignLeadReviewItem[]
  summary: {
    approved: number
    flagged: number
    needs_validation: number
    rejected: number
    included: number
    excluded: number
    total: number
  }
}

async function getCampaignLeadReview(campaignId: number) {
  return baseAPICall<CampaignLeadReviewResponse>(() =>
    clientInstance.get(`contacts/campaigns/${campaignId}/lead-review/`)
  )
}

async function updateCampaignLeadReview(
  campaignId: number,
  values: { contact_ids: number[]; is_included: boolean }
) {
  return baseAPICall<{ success: boolean }>(() =>
    clientInstance.patch(
      `contacts/campaigns/${campaignId}/lead-review/`,
      values
    )
  )
}

export const useGetCampaignLeadReview = (
  campaignId: number,
  disabled?: boolean
) =>
  useQuery({
    queryKey: ['campaign-lead-review', campaignId],
    queryFn: () => getCampaignLeadReview(campaignId),
    enabled: campaignId > 0 && !disabled,
    // Recipient counts (sidebar, preview, launch readiness) all derive from
    // this query. Always-refetch on mount keeps them honest across step
    // navigation and after edits made elsewhere (contact email changes,
    // includes, bison syncs) instead of waiting for launch-time to converge.
    // This overrides the global `refetchOnMount: false` default for this
    // query only — freshness elsewhere is driven by explicit invalidation.
    refetchOnMount: 'always',
  })

export const useUpdateCampaignLeadReview = (campaignId: number) =>
  useMutation({
    mutationFn: (values: { contact_ids: number[]; is_included: boolean }) =>
      updateCampaignLeadReview(campaignId, values),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['campaign-lead-review', campaignId],
      })
    },
  })

/* -------------
Campaign Refresh Status
-------------- */
export interface CampaignRefreshStatusResponse {
  is_refreshing: boolean
}

export async function getCampaignRefreshStatus(signal?: AbortSignal) {
  return baseAPICall<CampaignRefreshStatusResponse>(() =>
    clientInstance.get('contacts/campaigns/refresh-status/', { signal })
  )
}

export const useGetCampaignRefreshStatus = (options?: {
  enabled?: boolean
  refetchInterval?: number
}) =>
  useQuery({
    queryKey: ['campaigns-refresh-status'],
    queryFn: ({ signal }) => getCampaignRefreshStatus(signal),
    refetchInterval: options?.refetchInterval ?? 5000,
    enabled: options?.enabled ?? true,
    staleTime: 0,
    refetchOnMount: 'always',
  })

/* -------------
Create Campaign V2
-------------- */
type CreateCampaignV2Params = {
  name: string
  type_slug?: CompanyTypeSlug
  product_id?: number | null
  color?: string
  description?: string
  launch_date?: string | null
  owner?: number | null
  notification_recipient_ids?: number[]
}

async function createCampaignV2(values: CreateCampaignV2Params) {
  return baseAPICall<Campaign>(
    () => clientInstance.post('contacts/campaigns/create/', values),
    { successMessage: 'Campaign created successfully' }
  )
}

export const useCreateCampaignV2 = () => {
  return useMutation({
    mutationFn: createCampaignV2,
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [ALL_CAMPAIGNS_TABLE_KEY],
        exact: false,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ALL_CAMPAIGNS_TABLE_KEY],
      })
      queryClient.invalidateQueries({ queryKey: getCampaignStatsQueryKey() })
    },
  })
}

/* -------------
Per-row + bulk Email Validation (FIR-7121)
-------------- */

export interface CampaignValidationJob {
  id: number
  campaign_id: number
  contact_count: number
  processed_count: number
  status: 'pending' | 'in_progress' | 'completed' | 'failed'
  started_at: string | null
  completed_at: string | null
  error_message: string | null
}

async function validateContactEmails(campaignId: number, contactIds: number[]) {
  return baseAPICall<{ updated_count: number }>(() =>
    clientInstance.post(
      `contacts/campaigns/${campaignId}/validate-contact-emails/`,
      { contact_ids: contactIds }
    )
  )
}

async function startCampaignValidationJob(campaignId: number) {
  return baseAPICall<CampaignValidationJob>(() =>
    clientInstance.post(`contacts/campaigns/${campaignId}/validation-jobs/`, {})
  )
}

// Returns the latest job in ANY status (or null when the campaign has never
// run one) so the UI can report completed/failed outcomes — "running" is
// derived from job.status, not from the row's presence.
async function getActiveCampaignValidationJob(campaignId: number) {
  // baseAPICall unwraps response.data itself; a 204 body unwraps to ''.
  // Returning null (never undefined) keeps React Query's cache valid.
  const job = await baseAPICall<CampaignValidationJob | ''>(() =>
    clientInstance.get(
      `contacts/campaigns/${campaignId}/validation-jobs/active/`
    )
  )
  return job || null
}

export const useValidateContactEmails = (campaignId: number) =>
  useMutation({
    mutationFn: (contactIds: number[]) =>
      validateContactEmails(campaignId, contactIds),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['campaign-lead-review', campaignId],
      })
    },
  })

export const useStartCampaignValidationJob = (campaignId: number) =>
  useMutation({
    mutationFn: () => startCampaignValidationJob(campaignId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['campaign-validation-job', campaignId],
      })
      // Sub-2s jobs can finish before the polling hook's first refetch,
      // leaving its transition detector unable to fire. Schedule a
      // catch-up invalidation. Invalidations on unmounted queries are
      // no-ops in React Query, so leaving the screen mid-mutation is safe.
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ['campaign-lead-review', campaignId],
        })
        queryClient.invalidateQueries({
          queryKey: ['campaign-validation-job', campaignId],
        })
      }, 3000)
    },
  })

// Snapshot of the last-observed job per campaign. Module-level (not a
// component ref) so wizard-step navigation mid-run can't swallow the
// completion toast: if the job finishes between one step unmounting and the
// next mounting, the new observer still knows it previously saw the job
// running. Reset only on full page load, which is exactly when a terminal
// job should read as history rather than news.
const lastSeenValidationJobByCampaign = new Map<number, JobSnapshot>()

export const useActiveCampaignValidationJob = (
  campaignId: number,
  options?: { enabled?: boolean }
) =>
  useQuery({
    queryKey: ['campaign-validation-job', campaignId],
    queryFn: async () => {
      const job = await getActiveCampaignValidationJob(campaignId)
      // Transition semantics (incl. sub-2s jobs that finish before the poller
      // ever sees them running) live in evaluateJobTransition — see its tests.
      const outcome = evaluateJobTransition(
        lastSeenValidationJobByCampaign.get(campaignId),
        job
      )
      lastSeenValidationJobByCampaign.set(campaignId, {
        jobId: job?.id ?? null,
        running: isJobRunning(job),
      })
      if (outcome !== null && job !== null) {
        // The user just watched this job run — tell them how it ended.
        if (outcome === 'completed') {
          // fetchQuery (not invalidate) so the fresh summary both updates the
          // table and tells us whether the provider left contacts unverified —
          // a "finished" toast while everything stays Needs-validation reads
          // as a silent failure.
          const review = await queryClient
            .fetchQuery({
              queryKey: ['campaign-lead-review', campaignId],
              queryFn: () => getCampaignLeadReview(campaignId),
            })
            .catch(() => null)
          if (review === null) {
            // Summary fetch failed — we can't tell whether contacts were
            // verified, so don't claim success. Fall back to invalidation so
            // the table catches up on its own refetch.
            queryClient.invalidateQueries({
              queryKey: ['campaign-lead-review', campaignId],
            })
            toast.info(
              'Email validation finished — refresh to see updated results'
            )
          } else if (review.summary.needs_validation > 0) {
            const remaining = review.summary.needs_validation
            toast.warning(
              `Validation finished, but ${remaining} contact${
                remaining === 1 ? '' : 's'
              } couldn't be verified and remain${remaining === 1 ? 's' : ''} excluded. You can retry or reach out to them directly.`
            )
          } else {
            toast.success(
              `Email validation finished — ${job.processed_count} contact${
                job.processed_count === 1 ? '' : 's'
              } checked`
            )
          }
        } else {
          queryClient.invalidateQueries({
            queryKey: ['campaign-lead-review', campaignId],
          })
          toast.error(
            job.error_message
              ? `Email validation failed: ${job.error_message}`
              : 'Email validation failed — please try again'
          )
        }
      }
      return job
    },
    enabled: options?.enabled ?? campaignId > 0,
    // The global refetchOnMount:false would serve a stale pre-job cache when
    // the user leaves and re-enters the step mid-run — the UI would show
    // "Validate all" while a job is still running and never resume polling.
    refetchOnMount: 'always',
    refetchInterval: (query) => (isJobRunning(query.state.data) ? 2000 : false),
  })
